// ResearchDossier — refined formatting:
// - Header with query + badges (confidence, risk, cost, latency, tokens, badges)
// - Collapsible sections: Executive Summary, Findings, Limitations, Citations
// - Markdown rendering with remark-gfm
// - Inline [n] citation chips
// - Export PDF / MD / JSON
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ExternalLink,
  AlertTriangle,
  ChevronDown,
  Download,
  FileJson,
  FileType,
  FileText as FileTextIcon,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { Dossier } from "@/lib/deep-research/types";
import { useResearch } from "@/store/researchStore";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function CitationChip({ n, onClick }: { n: number; onClick: (n: number) => void }) {
  return (
    <button
      onClick={() => onClick(n)}
      className="inline-flex items-center justify-center mx-0.5 px-1 py-0 rounded text-[10px] font-mono bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 align-baseline"
    >
      [{n}]
    </button>
  );
}

// Render markdown then post-process [n] citations
function MarkdownWithCitations({
  text,
  onCite,
}: {
  text: string;
  onCite: (n: number) => void;
}) {
  return (
    <div className="prose prose-invert prose-sm max-w-none prose-headings:font-display prose-headings:font-semibold prose-p:leading-relaxed prose-p:text-[13px] prose-li:text-[13px] prose-code:text-[12px] prose-code:bg-surface-2 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-strong:text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p>{renderCitations(children, onCite)}</p>,
          li: ({ children }) => <li>{renderCitations(children, onCite)}</li>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function renderCitations(node: React.ReactNode, onCite: (n: number) => void): React.ReactNode {
  if (typeof node === "string") {
    const parts: React.ReactNode[] = [];
    const re = /\[(\d+)\]/g;
    let idx = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(node))) {
      if (m.index > idx) parts.push(node.slice(idx, m.index));
      parts.push(<CitationChip key={`c-${m.index}`} n={Number(m[1])} onClick={onCite} />);
      idx = m.index + m[0].length;
    }
    if (idx < node.length) parts.push(node.slice(idx));
    return parts;
  }
  if (Array.isArray(node)) return node.map((c, i) => <span key={i}>{renderCitations(c, onCite)}</span>);
  return node;
}

function dossierToMarkdown(d: Dossier): string {
  const lines: string[] = [];
  lines.push(`# Deep Research Dossier`);
  lines.push(``);
  lines.push(`> **Query:** ${d.query}`);
  lines.push(``);
  lines.push(`**Confidence:** ${(d.overallConfidence * 100).toFixed(1)}% · **Tokens:** ${d.totalTokens.toLocaleString()} · **Cost:** $${d.totalCostUsd.toFixed(4)} · **Latency:** ${Math.round(d.totalLatencyMs)}ms`);
  if (d.badges?.length) lines.push(`**Badges:** ${d.badges.join(", ")}`);
  lines.push(``);
  lines.push(`## Executive Summary`);
  lines.push(d.executiveSummary);
  lines.push(``);
  for (const s of d.sections) {
    lines.push(`## ${s.heading}`);
    lines.push(s.body);
    lines.push(``);
  }
  if (d.limitations.length) {
    lines.push(`## Limitations`);
    d.limitations.forEach((l) => lines.push(`- ${l}`));
    lines.push(``);
  }
  lines.push(`## Citations`);
  d.citations.forEach((c, i) => {
    lines.push(`${i + 1}. **${c.title}** — ${c.authors?.join(", ") ?? "—"}${c.publishedAt ? ` (${c.publishedAt})` : ""}. ${c.source}`);
  });
  return lines.join("\n");
}

export function ResearchDossier({ dossier }: { dossier: Dossier }) {
  const setSelected = useResearch((s) => s.setSelectedCitation);
  const selected = useResearch((s) => s.selectedCitation);
  const [openSummary, setOpenSummary] = useState(true);
  const [openSections, setOpenSections] = useState(true);
  const [openLimits, setOpenLimits] = useState(true);
  const [openCites, setOpenCites] = useState(true);

  const selectedDoc = useMemo(
    () => (selected != null && dossier.citations[selected - 1]) || null,
    [selected, dossier.citations],
  );

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(dossier, null, 2)], { type: "application/json" });
    triggerDownload(blob, `dossier-${Date.now()}.json`);
    toast.success("JSON exportado");
  };
  const exportMd = () => {
    const md = dossierToMarkdown(dossier);
    const blob = new Blob([md], { type: "text/markdown" });
    triggerDownload(blob, `dossier-${Date.now()}.md`);
    toast.success("Markdown exportado");
  };
  const exportPdf = () => {
    // Lightweight PDF via print-friendly window (no external deps)
    const md = dossierToMarkdown(dossier);
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Dossier</title>
      <style>body{font-family:ui-sans-serif,system-ui;max-width:760px;margin:40px auto;padding:0 20px;color:#111;line-height:1.55}h1{border-bottom:2px solid #333;padding-bottom:8px}h2{margin-top:28px}blockquote{border-left:3px solid #888;padding-left:10px;color:#555}code{background:#f3f3f3;padding:2px 4px;border-radius:3px}</style>
      </head><body><pre style="white-space:pre-wrap;font-family:inherit">${md.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]!))}</pre>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Bloqueador de popup impediu o PDF");
      return;
    }
    w.document.write(html);
    w.document.close();
    toast.success("Janela de impressão aberta — escolha Salvar como PDF");
  };

  const confColor =
    dossier.overallConfidence >= 0.75 ? "bg-success/15 text-success border-success/30" :
    dossier.overallConfidence >= 0.5  ? "bg-warning/15 text-warning border-warning/30" :
                                         "bg-destructive/15 text-destructive border-destructive/30";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-xl glass border border-secondary/30 p-4 space-y-3"
      data-testid="research-dossier"
    >
      {/* HEADER */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-wider text-secondary">
              Deep Research Dossier
            </div>
            <h1 className="text-lg font-display font-bold mt-0.5 leading-tight break-words">
              {dossier.query}
            </h1>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="outline" className="h-7 gap-1 text-[10px]" onClick={exportPdf} data-testid="export-pdf">
              <FileTextIcon className="w-3 h-3" /> PDF
            </Button>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-[10px]" onClick={exportMd} data-testid="export-md">
              <FileType className="w-3 h-3" /> MD
            </Button>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-[10px]" onClick={exportJson} data-testid="export-json">
              <FileJson className="w-3 h-3" /> JSON
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <Badge variant="outline" className={cn("text-[10px] font-mono", confColor)}>
            conf {Math.round(dossier.overallConfidence * 100)}%
          </Badge>
          <Badge variant="outline" className="text-[10px] font-mono">
            tokens {dossier.totalTokens.toLocaleString()}
          </Badge>
          <Badge variant="outline" className="text-[10px] font-mono">
            cost ${dossier.totalCostUsd.toFixed(4)}
          </Badge>
          <Badge variant="outline" className="text-[10px] font-mono">
            latency {Math.round(dossier.totalLatencyMs)}ms
          </Badge>
          {dossier.domain && (
            <Badge variant="outline" className="text-[10px] font-mono bg-info/10 text-info border-info/30">
              {dossier.domain.primary}
            </Badge>
          )}
          {(dossier.badges ?? []).map((b) => (
            <Badge
              key={b}
              variant="outline"
              className={cn(
                "text-[10px] font-mono",
                b === "TOPIC_DRIFT_DETECTED" && "bg-warning/15 text-warning border-warning/30",
                b === "DEGRADED" && "bg-destructive/15 text-destructive border-destructive/30",
                b === "INSUFFICIENT_SOURCES" && "bg-destructive/15 text-destructive border-destructive/30",
              )}
            >
              {b}
            </Badge>
          ))}
        </div>
      </div>

      {/* EXECUTIVE SUMMARY */}
      <Collapsible open={openSummary} onOpenChange={setOpenSummary}>
        <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left group">
          <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", !openSummary && "-rotate-90")} />
          <span className="text-[11px] font-mono uppercase tracking-wider text-primary group-hover:text-primary-glow">
            Executive Summary
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 pl-5">
          <MarkdownWithCitations text={dossier.executiveSummary} onCite={setSelected} />
        </CollapsibleContent>
      </Collapsible>

      {/* SECTIONS */}
      <Collapsible open={openSections} onOpenChange={setOpenSections}>
        <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left group">
          <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", !openSections && "-rotate-90")} />
          <span className="text-[11px] font-mono uppercase tracking-wider text-primary group-hover:text-primary-glow">
            Key Findings ({dossier.sections.length})
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 pl-5 space-y-3">
          {dossier.sections.map((s) => (
            <div key={s.nodeId} className="rounded-lg border border-border bg-surface-2/40 p-3">
              <h3 className="text-[13px] font-display font-semibold text-foreground mb-1.5">{s.heading}</h3>
              <MarkdownWithCitations text={s.body} onCite={setSelected} />
              {s.claims.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                  {s.claims.slice(0, 5).map((c) => (
                    <div key={c.id} className="flex items-start gap-2 text-[11px]">
                      <div
                        className="mt-1 h-1 rounded-full shrink-0 w-12"
                        style={{
                          background: `linear-gradient(90deg, hsl(${Math.round(c.confidence * 120)} 70% 50%) ${c.confidence * 100}%, hsl(var(--border)) ${c.confidence * 100}%)`,
                        }}
                        title={`confidence ${Math.round(c.confidence * 100)}%`}
                      />
                      <span className="text-muted-foreground">{c.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>

      {/* LIMITATIONS */}
      {dossier.limitations.length > 0 && (
        <Collapsible open={openLimits} onOpenChange={setOpenLimits}>
          <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left group">
            <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", !openLimits && "-rotate-90")} />
            <span className="text-[11px] font-mono uppercase tracking-wider text-warning group-hover:text-warning/80 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Limitations ({dossier.limitations.length})
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2 pl-5">
            <ul className="text-[12px] text-muted-foreground space-y-1 list-disc pl-4">
              {dossier.limitations.map((l, i) => <li key={i}>{l}</li>)}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* CITATIONS */}
      <Collapsible open={openCites} onOpenChange={setOpenCites}>
        <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left group">
          <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", !openCites && "-rotate-90")} />
          <span className="text-[11px] font-mono uppercase tracking-wider text-secondary group-hover:text-secondary/80">
            Citations ({dossier.citations.length})
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 pl-5">
          <ol className="space-y-1.5">
            {dossier.citations.map((c, i) => {
              const n = i + 1;
              const isSel = selected === n;
              const arxivMatch = /arxiv\.org\/abs\/(\d{4}\.\d+)/.exec(c.source);
              return (
                <li
                  key={c.id}
                  className={cn(
                    "text-[11px] font-mono flex items-start gap-2 p-2 rounded border",
                    isSel ? "border-primary/50 bg-primary/10" : "border-border bg-surface-2/40",
                  )}
                >
                  <span className="text-primary tabular-nums shrink-0">[{n}]</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground font-display text-[12px] font-medium leading-snug">{c.title}</div>
                    <div className="text-muted-foreground text-[10px] mt-0.5">
                      {c.authors?.slice(0, 4).join(", ") ?? "—"}
                      {c.publishedAt ? ` · ${c.publishedAt}` : ""}
                      {arxivMatch ? ` · arXiv:${arxivMatch[1]}` : ""}
                    </div>
                    <a
                      href={c.source.startsWith("http") ? c.source : `https://${c.source.replace(/^https?:\/\//, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-info hover:underline text-[10px] mt-0.5 truncate max-w-full"
                    >
                      <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{c.source}</span>
                    </a>
                    {c.hopChain && c.hopChain.length > 1 && (
                      <div className="text-[9px] text-muted-foreground/80 mt-0.5">
                        hop chain: {c.hopChain.join(" → ")}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelected(isSel ? null : n)}
                    className="text-[10px] text-muted-foreground hover:text-primary"
                    title="Highlight"
                  >
                    <Download className="w-3 h-3" style={{ display: "none" }} />
                    {isSel ? "✓" : "○"}
                  </button>
                </li>
              );
            })}
          </ol>
        </CollapsibleContent>
      </Collapsible>

      {selectedDoc && (
        <div className="text-[11px] font-mono p-2 rounded border border-primary/40 bg-primary/5">
          <span className="text-primary">[{selected}]</span> {selectedDoc.snippet}
        </div>
      )}
    </motion.div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
